import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import config from "../../config";
import { formatApiError } from "../../utilities/apiErrorMessages";
import { coalesceAsync, invalidateCoalescedAsync } from "../../utilities/coalesceAsync";
const DENTISTS_LIST_CACHE_KEY = "get-dentists:";

const resolveList = (payload) => {
    if (Array.isArray(payload)) {
        return payload;
    }

    if (Array.isArray(payload?.data?.data)) {
        return payload.data.data;
    }

    if (Array.isArray(payload?.data)) {
        return payload.data;
    }

    return [];
};

export const GetDentists = createAsyncThunk("dentist/GetDentists", async ({ token, userTypeId, filters }, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;
    try {
        const url = new URL(`${config.apiUrl}/users`);
        if (userTypeId) {
            url.searchParams.set('user_type_id', userTypeId);
        }

        if (filters && typeof filters === 'object') {
            Object.entries(filters).forEach(([k, v]) => {
                if (v === undefined || v === null || v === '') {
                    return;
                }
                if (Array.isArray(v)) {
                    if (v.length === 0) return;
                    url.searchParams.set(k, v.join(','));
                    return;
                }
                url.searchParams.set(k, String(v));
            });
        }

        if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.debug('[GetDentists] url=', url.toString());
        }

        const data = await coalesceAsync(
            `get-dentists:${url.toString()}`,
            async () => {
                const response = await fetch(url.toString(), {
                    method: "GET",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });

                const body = await response.json();
                if (!response.ok) {
                    const error = new Error(body?.message ?? "Unable to fetch dentists");
                    error.payload = body;
                    throw error;
                }

                return body;
            },
            { ttlMs: 1500 }
        );

        return fulfillWithValue(data);
    } catch (error) {
        if (error?.payload) {
            return rejectWithValue(error.payload);
        }
        return rejectWithValue({ message: error?.message ?? "Unable to fetch dentists" });
    }
});

export const AddDentist = createAsyncThunk("dentist/AddDentist", async ({ token, values, userTypeId }, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;
    try {
        const response = await fetch(`${config.apiUrl}/users`, {
            method: "POST",
            body: JSON.stringify({ ...values, user_type_id: userTypeId }),
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        });

        const data = await response.json();
        if (!response.ok) {
            return rejectWithValue(data);
        }

        invalidateCoalescedAsync(DENTISTS_LIST_CACHE_KEY);
        return fulfillWithValue(data);
    } catch (error) {
        return rejectWithValue({ message: error.message });
    }
});

export const UpdateDentist = createAsyncThunk("dentist/UpdateDentist", async ({ token, id, values, userTypeId }, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;

    try {
        const payload = { ...values };
        if (userTypeId) {
            payload.user_type_id = userTypeId;
        }

        const response = await fetch(`${config.apiUrl}/users/${id}`, {
            method: "PUT",
            body: JSON.stringify(payload),
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        });

        const data = await response.json();
        if (!response.ok) {
            return rejectWithValue(data);
        }

        return fulfillWithValue(data);
    } catch (error) {
        return rejectWithValue({ message: error.message });
    }
});

export const DeleteDentist = createAsyncThunk("dentist/DeleteDentist", async ({ token, id }, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;
    try {
        const response = await fetch(`${config.apiUrl}/users/${id}`, {
            method: "DELETE",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        });

        const data = response.status === 204 ? {} : await response.json();
    invalidateCoalescedAsync(DENTISTS_LIST_CACHE_KEY);

        if (!response.ok) {
            return rejectWithValue(data);
        }

        return fulfillWithValue({ id, message: data?.message ?? null });
    } catch (error) {
        return rejectWithValue({ message: error.message });
    }
});

export const DentistSlice = createSlice({
    name: "dentist",
    initialState: {
        dentists: [],
        loading: false,
        error: null,
        successMessage: null,
        selectedDentist: null
    },
    reducers: {
        selectDentist: (state, action) => {
            state.selectedDentist = action.payload
        },
        clearDentistFeedback: (state) => {
            state.error = null
            state.successMessage = null
        }
    },

    extraReducers: {

        //GetDentists actions
        [GetDentists.pending]: (state, action) => {
            state.loading = true
            state.successMessage = null
            state.error = null
        },
        [GetDentists.fulfilled]: (state, action) => {
            state.loading = false
            state.dentists = resolveList(action.payload)
            state.successMessage = null
            state.error = null

        },
        [GetDentists.rejected]: (state, action) => {
            state.loading = false
            state.successMessage = null
            state.error = formatApiError(action.payload, 'Something went wrong')
        },

        [AddDentist.pending]: (state, action) => {
            state.loading = true
            state.successMessage = null
            state.error = null
        },
        [AddDentist.fulfilled]: (state, action) => {
            state.loading = false
            state.successMessage = action.payload.message
            state.error = null
            const created = action.payload.data
            if (created) {
                const current = Array.isArray(state.dentists) ? state.dentists : []
                state.dentists = [...current, created]
            }
        },
        [AddDentist.rejected]: (state, action) => {
            state.loading = false
            state.successMessage = null
            state.error = formatApiError(action.payload, 'Something went wrong')
        },

        [UpdateDentist.pending]: (state, action) => {
            state.loading = true
            state.successMessage = null
            state.error = null
        },
        [UpdateDentist.fulfilled]: (state, action) => {
            state.loading = false
            state.successMessage = action.payload.message
            state.error = null
            const updated = action.payload.data
            if (updated) {
                const index = state.dentists.findIndex(dentist => dentist.id === updated.id);
                if (index !== -1) {
                    state.dentists[index] = {
                        ...state.dentists[index],
                        ...updated,
                    };
                }
            }
        },
        [UpdateDentist.rejected]: (state, action) => {

            state.loading = false
            state.successMessage = null
            state.error = formatApiError(action.payload, 'Something went wrong')
        },

        //DeleteDentist actions

        [DeleteDentist.pending]: (state, action) => {
            state.loading = true
            state.successMessage = null
            state.error = null
        },
        [DeleteDentist.fulfilled]: (state, action) => {
            state.loading = false
            state.successMessage = action.payload.message
            state.error = null
            const index = state.dentists.findIndex(({ id }) => id === action.payload.id);
            if (index !== -1) {
                state.dentists.splice(index, 1);
            }
        },
        [DeleteDentist.rejected]: (state, action) => {

            state.loading = false
            state.successMessage = null
            state.error = formatApiError(action.payload, 'Something went wrong')
        },
    }
})

export default DentistSlice.reducer;
export const { selectDentist, clearDentistFeedback } = DentistSlice.actions